import * as RadixSelect from "@radix-ui/react-select";
import clsx from "clsx";
import { forwardRef } from "react";
import classes from "./select.module.css";

export type SelectProps = RadixSelect.SelectTriggerProps & {
  onSelect: (value: string) => void;
  placeholder: string;
  icon?: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  iconClassName?: string;
  value?: string;
  displayValue?: string;
  contentClassName?: string;
};

export type OnSelectHandler = SelectProps["onSelect"];

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      onSelect,
      disabled,
      className,
      children,
      placeholder,
      icon,
      onOpenChange,
      iconClassName,
      value,
      displayValue,
      contentClassName,
      ...props
    },
    ref,
  ) => {
    return (
      <RadixSelect.Root
        onValueChange={onSelect}
        disabled={disabled}
        onOpenChange={onOpenChange}
        value={value}
      >
        <RadixSelect.Trigger
          ref={ref}
          className={clsx(classes.trigger, className)}
          {...props}
          data-testid="select-trigger"
          aria-label={placeholder}
        >
          <span className={classes.valueContainer}>
            <RadixSelect.Value placeholder={placeholder}>
              {displayValue}
            </RadixSelect.Value>
          </span>
          <RadixSelect.Icon
            className={clsx(classes.iconContainer, iconClassName)}
          >
            {icon}
          </RadixSelect.Icon>
        </RadixSelect.Trigger>
        <RadixSelect.Portal>
          <RadixSelect.Content
            className={clsx(classes.content, contentClassName)}
            position="popper"
            align="start"
          >
            <RadixSelect.ScrollUpButton className={classes.scrollButton}>
              <span className={classes.rotateIcon}>{icon}</span>
            </RadixSelect.ScrollUpButton>
            <RadixSelect.Viewport className={classes.viewport}>
              {children}
            </RadixSelect.Viewport>
            <RadixSelect.ScrollDownButton className={classes.scrollButton}>
              {icon}
            </RadixSelect.ScrollDownButton>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
    );
  },
);

Select.displayName = "Select";

export type OptionProps = RadixSelect.SelectItemProps & {
  itemIndicatorContent?: string | React.ReactNode;
};

export const Option = forwardRef<HTMLDivElement, OptionProps>(
  ({ children, className, itemIndicatorContent, ...props }, ref) => {
    return (
      <RadixSelect.Item
        ref={ref}
        className={clsx(classes.item, className)}
        {...props}
      >
        {itemIndicatorContent && (
          <RadixSelect.ItemIndicator className={classes.itemIndicator}>
            {itemIndicatorContent}
          </RadixSelect.ItemIndicator>
        )}
        <div className={classes.itemContent}>
          <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
        </div>
      </RadixSelect.Item>
    );
  },
);

Option.displayName = "Option";
